import { useCallback, useMemo } from "react";
import type { CashierPaymentMethod } from "@/api/cashier";
import type { UploadedFile } from "@/components/bank-statement-upload/BankStatementUpload";
import { ErrorMessage } from "@/components/ErrorMessage";
import {
  DynamicForm,
  type DynamicFormProps,
} from "@/components/forms/dynamic-form/DynamicForm";
import type { NuveiCardFieldsChange } from "@/components/forms/nuvei-new-credit-card-widget/NuveiNewCreditCardWidget";
import { useDynamicForm } from "@/hooks/deposit/useDynamicForm";
import { useCreditCardForm } from "@/hooks/useCreditCardForm";
import { getPaymentMethodFormFields } from "@/utils/helpers";
import { createOptionConstraintsFilter } from "@/utils/optionConstraintsFilter";

export type PaymentFormFieldsProps = {
  licenceId: string;
  paymentMethod: CashierPaymentMethod;
  errorMessage?: string;
  className?: string;
  formClassName?: string;
  bankStatementFile?: UploadedFile;
  onBankStatementFileChange?: (file: UploadedFile | undefined) => void;
};

export const PaymentFormFields = ({
  className,
  formClassName,
  licenceId,
  paymentMethod,
  errorMessage,
  bankStatementFile,
  onBankStatementFileChange,
}: PaymentFormFieldsProps) => {
  const { updateFormData } = useDynamicForm();
  const { updateCreditCardFormData } = useCreditCardForm();

  const fields = getPaymentMethodFormFields(paymentMethod, licenceId);

  const onDynamicFormChanges = useCallback<
    Required<DynamicFormProps>["onChanges"]
  >(
    (values) => updateFormData(paymentMethod.id, values),
    [updateFormData, paymentMethod.id]
  );

  const onCreditCardChange = useCallback(
    (change: NuveiCardFieldsChange) => {
      const formValid = Boolean(
        !change.isEmpty && !change.hasError && change.nameOnCard
      );
      updateCreditCardFormData({
        formValid,
        nameOnCard: change.nameOnCard,
      });
    },
    [updateCreditCardFormData]
  );

  const optionsFilter = useMemo(
    () => (fields ? createOptionConstraintsFilter(fields) : undefined),
    [fields]
  );

  if (!licenceId || !fields) return null;

  return (
    <div className={className}>
      {fields?.length ? (
        <DynamicForm
          key={paymentMethod.id}
          id={paymentMethod.id}
          fields={fields}
          className={formClassName}
          optionsFilter={optionsFilter}
          onChanges={onDynamicFormChanges}
          onCreditCardChange={onCreditCardChange}
          bankStatementFile={bankStatementFile}
          onBankStatementFileChange={onBankStatementFileChange}
        />
      ) : null}

      {errorMessage && (
        <div className="ps-mb-4">
          <ErrorMessage message={errorMessage} />
        </div>
      )}
    </div>
  );
};
